import { type FrontKind } from "@nav-games/weather";

/// Vertical cross-section through a front, showing the slope of the
/// boundary between warm and cold air, the cloud sequence and the
/// surface position. Pedagogically simplified to be readable rather
/// than meteorologically precise.

interface Props {
  kind: FrontKind;
  size?: number;
}

const VW = 480;
const VH = 240;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const WARM = "#f5b400";
const COLD = "#1e6091";
const CLOUD = "#cbd5e1";

export function FrontCrossSectionFigure({ kind, size = VW }: Props) {
  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Cross-section through a ${kind} front`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Ground line. */}
      <line
        x1={0}
        y1={VH - 20}
        x2={VW}
        y2={VH - 20}
        stroke={STROKE}
        strokeWidth={2}
      />
      <text x={6} y={VH - 6} fontSize={10} fill={MUTED}>
        Surface
      </text>

      {/* Direction-of-motion arrow at the top. */}
      <line
        x1={VW - 80}
        y1={20}
        x2={VW - 20}
        y2={20}
        stroke={STROKE}
        strokeWidth={1}
        markerEnd="url(#arrowhead)"
      />
      <text
        x={VW - 50}
        y={14}
        fontSize={10}
        fill={STROKE}
        textAnchor="middle"
      >
        Direction of movement
      </text>
      <defs>
        <marker
          id="arrowhead"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto"
        >
          <polygon points="0,0 10,5 0,10" fill={STROKE} />
        </marker>
      </defs>

      {kind === "warm" && <WarmFront />}
      {kind === "cold" && <ColdFront />}
      {kind === "occluded" && <OccludedFront />}
    </svg>
  );
}

function WarmFront() {
  // Shallow slope (~1:150 in reality; we draw at 1:6 for readability)
  // sloping up from right-to-left. Warm air rides over cold air.
  const groundY = VH - 20;
  return (
    <>
      {/* Cold air wedge — bottom right, blue. */}
      <polygon
        points={`${VW},${groundY} ${VW},${groundY - 100} 80,${groundY}`}
        fill={`${COLD}20`}
      />
      {/* Warm air — overhead pushing in from the right, sloping up to the left. */}
      <polygon
        points={`60,40 ${VW},40 ${VW},${groundY - 100} 80,${groundY}`}
        fill={`${WARM}18`}
      />
      {/* Frontal slope. */}
      <line
        x1={80}
        y1={groundY}
        x2={VW}
        y2={groundY - 100}
        stroke={WARM}
        strokeWidth={2.5}
      />
      {/* Cloud band above the slope. */}
      <CloudArc
        x1={80}
        y1={groundY - 25}
        x2={140}
        y2={groundY - 55}
        x3={220}
        y3={groundY - 80}
        x4={340}
        y4={groundY - 100}
        x5={VW - 20}
        y5={groundY - 115}
      />

      {/* Labels. */}
      <text x={20} y={groundY - 50} fontSize={11} fontWeight={600} fill={COLD}>
        COLD air
      </text>
      <text
        x={VW - 100}
        y={groundY - 80}
        fontSize={11}
        fontWeight={600}
        fill={WARM}
      >
        WARM air
      </text>

      {/* Cloud layer labels along the slope. */}
      <text x={VW - 60} y={70} fontSize={9} fill={MUTED}>
        Cirrus
      </text>
      <text x={VW - 160} y={90} fontSize={9} fill={MUTED}>
        Cirrostratus
      </text>
      <text x={VW - 260} y={120} fontSize={9} fill={MUTED}>
        Altostratus
      </text>
      <text x={130} y={155} fontSize={9} fill={MUTED}>
        Nimbostratus
      </text>

      {/* Surface front symbol — semicircle pointing left (direction of motion). */}
      <FrontSymbol x={80} y={groundY} kind="warm" />
      <text x={80} y={groundY + 14} fontSize={10} textAnchor="middle" fill={MUTED}>
        Surface front
      </text>

      {/* Rain area indicators. */}
      <RainShafts x1={90} x2={250} y={groundY - 5} />
    </>
  );
}

function ColdFront() {
  const groundY = VH - 20;
  // Steep slope (~1:50; drawn at 1:2). Cold air undercuts warm.
  return (
    <>
      {/* Cold air pushing in from the left, undercutting. */}
      <polygon
        points={`0,${groundY} 0,40 240,40 320,${groundY}`}
        fill={`${COLD}20`}
      />
      {/* Warm air retreating to the right. */}
      <polygon
        points={`240,40 ${VW},40 ${VW},${groundY} 320,${groundY}`}
        fill={`${WARM}18`}
      />
      {/* Frontal slope. */}
      <line
        x1={320}
        y1={groundY}
        x2={240}
        y2={40}
        stroke={COLD}
        strokeWidth={2.5}
      />

      {/* Cumulus + cumulonimbus along the front. */}
      <CumulusBlob cx={280} cy={groundY - 70} r={40} />
      <CumulusBlob cx={250} cy={groundY - 110} r={30} />
      <CumulusBlob cx={290} cy={groundY - 130} r={32} />
      {/* Anvil */}
      <ellipse cx={310} cy={50} rx={70} ry={10} fill={CLOUD} />

      {/* Labels. */}
      <text x={20} y={groundY - 30} fontSize={11} fontWeight={600} fill={COLD}>
        COLD air
      </text>
      <text
        x={VW - 100}
        y={groundY - 100}
        fontSize={11}
        fontWeight={600}
        fill={WARM}
      >
        WARM air
      </text>
      <text x={310} y={40} fontSize={9} fill={MUTED} textAnchor="middle">
        Cumulonimbus + anvil
      </text>

      <FrontSymbol x={320} y={groundY} kind="cold" />
      <text x={320} y={groundY + 14} fontSize={10} textAnchor="middle" fill={MUTED}>
        Surface front
      </text>

      <RainShafts x1={260} x2={340} y={groundY - 5} heavy />
    </>
  );
}

function OccludedFront() {
  const groundY = VH - 20;
  return (
    <>
      {/* Two cold airmasses sandwich a warm pocket aloft. */}
      <polygon
        points={`0,${groundY} 0,40 220,40 280,${groundY}`}
        fill={`${COLD}20`}
      />
      <polygon
        points={`280,${groundY} 380,40 ${VW},40 ${VW},${groundY}`}
        fill={`${COLD}15`}
      />
      {/* Warm air lifted aloft, no longer touching the surface. */}
      <polygon
        points={`220,40 380,40 320,90`}
        fill={`${WARM}20`}
      />
      {/* Two frontal lines meeting aloft. */}
      <line
        x1={280}
        y1={groundY}
        x2={220}
        y2={40}
        stroke={COLD}
        strokeWidth={2.5}
      />
      <line
        x1={280}
        y1={groundY}
        x2={380}
        y2={40}
        stroke={WARM}
        strokeWidth={2.5}
      />

      {/* Combined cloud blob. */}
      <CumulusBlob cx={260} cy={groundY - 90} r={36} />
      <CumulusBlob cx={300} cy={groundY - 110} r={32} />
      <ellipse cx={300} cy={60} rx={120} ry={14} fill={CLOUD} />

      <text x={20} y={groundY - 30} fontSize={11} fontWeight={600} fill={COLD}>
        COLD air
      </text>
      <text x={VW - 80} y={groundY - 30} fontSize={11} fontWeight={600} fill={COLD}>
        COLD air
      </text>
      <text x={300} y={75} fontSize={10} fontWeight={600} fill={WARM} textAnchor="middle">
        WARM air lifted aloft
      </text>

      <FrontSymbol x={280} y={groundY} kind="occluded" />
      <text x={280} y={groundY + 14} fontSize={10} textAnchor="middle" fill={MUTED}>
        Surface occlusion
      </text>

      <RainShafts x1={220} x2={340} y={groundY - 5} />
    </>
  );
}

function CloudArc({
  x1,
  y1,
  x2,
  y2,
  x3,
  y3,
  x4,
  y4,
  x5,
  y5,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  x3: number;
  y3: number;
  x4: number;
  y4: number;
  x5: number;
  y5: number;
}) {
  return (
    <g>
      <CumulusBlob cx={x1} cy={y1} r={20} />
      <CumulusBlob cx={x2} cy={y2} r={18} />
      <CumulusBlob cx={x3} cy={y3} r={18} />
      <CumulusBlob cx={x4} cy={y4} r={14} />
      <CumulusBlob cx={x5} cy={y5} r={10} />
    </g>
  );
}

function CumulusBlob({
  cx,
  cy,
  r,
}: {
  cx: number;
  cy: number;
  r: number;
}) {
  return (
    <g>
      <circle cx={cx - r * 0.5} cy={cy} r={r * 0.7} fill={CLOUD} />
      <circle cx={cx} cy={cy - r * 0.3} r={r} fill={CLOUD} />
      <circle cx={cx + r * 0.5} cy={cy + r * 0.1} r={r * 0.7} fill={CLOUD} />
    </g>
  );
}

function FrontSymbol({
  x,
  y,
  kind,
}: {
  x: number;
  y: number;
  kind: FrontKind;
}) {
  // Small representation of the symbol used on a synoptic chart.
  if (kind === "warm") {
    return (
      <g>
        <line x1={x - 10} y1={y - 4} x2={x + 10} y2={y - 4} stroke={WARM} strokeWidth={2} />
        <circle cx={x - 4} cy={y - 8} r={3} fill={WARM} />
        <circle cx={x + 4} cy={y - 8} r={3} fill={WARM} />
      </g>
    );
  }
  if (kind === "cold") {
    return (
      <g>
        <line x1={x - 10} y1={y - 4} x2={x + 10} y2={y - 4} stroke={COLD} strokeWidth={2} />
        <polygon points={`${x - 6},${y - 4} ${x - 3},${y - 10} ${x},${y - 4}`} fill={COLD} />
        <polygon points={`${x},${y - 4} ${x + 3},${y - 10} ${x + 6},${y - 4}`} fill={COLD} />
      </g>
    );
  }
  // occluded — purple-ish, both symbols on same side
  const OCC = "#7c3aed";
  return (
    <g>
      <line x1={x - 12} y1={y - 4} x2={x + 12} y2={y - 4} stroke={OCC} strokeWidth={2} />
      <circle cx={x - 8} cy={y - 8} r={3} fill={OCC} />
      <polygon points={`${x - 2},${y - 4} ${x + 1},${y - 10} ${x + 4},${y - 4}`} fill={OCC} />
      <circle cx={x + 8} cy={y - 8} r={3} fill={OCC} />
    </g>
  );
}

function RainShafts({
  x1,
  x2,
  y,
  heavy = false,
}: {
  x1: number;
  x2: number;
  y: number;
  heavy?: boolean;
}) {
  const lines: number[] = [];
  const step = heavy ? 8 : 14;
  for (let x = x1; x < x2; x += step) lines.push(x);
  return (
    <g>
      {lines.map((x, i) => (
        <line
          key={i}
          x1={x}
          y1={y - (heavy ? 30 : 20)}
          x2={x - 5}
          y2={y}
          stroke="#3b78b5"
          strokeWidth={heavy ? 1.5 : 1}
        />
      ))}
    </g>
  );
}
